// TRUST-2 — the /about founder note. THIS FILE IS THE AUTHORING PATH.
//
// WHAT THIS IS
// A short, signed note from the person who started Vidyapeeth360, rendered on /about directly
// under the company copy and above the "What you will never see on this site" block. It exists
// because the site's strongest asset — refusing to invent proof — is invisible until a named
// human says why the product is built the way it is.
//
// HOW TO PUBLISH IT (the whole procedure)
//   1. Get the real words from the founder. Name, role, 1–3 short paragraphs, and (optionally)
//      one paragraph answering "why India-first". Their words, approved by them — this is a
//      signed statement, so it is never drafted on their behalf and never AI-written.
//   2. Any FACTUAL statement in that copy (a year, a prior company, a school count, a
//      qualification, a place) needs a row in `docs/marketing/CLAIMS_REGISTRY.md` BEFORE it
//      ships — registry maintenance rule 4. Biography is exactly where unverifiable claims slip
//      onto a marketing site.
//   3. Replace `FOUNDER_NOTE = null` below with the object. Every field is required except
//      `whyIndiaFirst`; see FOUNDER_NOTE_SHAPE for the exact contract.
//   4. Run `cd frontend && npx jest --testPathPatterns "founderNote"`. The guard test proves a
//      half-filled note cannot ship. (TECH-6: was react-scripts + --testPathPattern; the runner is
//      gone and jest 30 renamed the flag to the plural --testPathPatterns.)
//   5. Nothing else changes: /about is prerendered from `LegalPage.js`, which already renders
//      this section when — and only when — the guard passes.
//
// WHY IT IS A CONSTANT AND NOT CMS COPY
// The rest of /about is a CMS-published legal document (`core/legal_defaults.py` `about`,
// overridable in PlatformAdmin). A personal, signed statement is deliberately NOT operator-
// editable at runtime: it should move through code review with the claims-registry row attached,
// the same as any other factual assertion on this site.
//
// UNTIL THEN, NOTHING RENDERS. The default is `null` and the guard fails closed, so /about ships
// today without a founder section rather than with an invented one.

/**
 * The shape `FOUNDER_NOTE` must satisfy. Kept as data (not a comment) so the test and the
 * authoring instructions can never drift apart.
 *
 *   name          string   The founder's real name, as they want it printed.
 *   role          string   Their title, e.g. "Founder" or "Founder & CEO".
 *   note          string[] 1–3 paragraphs in their own approved words.
 *   whyIndiaFirst string?  Optional extra paragraph; omitted or "" simply renders nothing.
 */
export const FOUNDER_NOTE_SHAPE = Object.freeze({
  name: "string (required)",
  role: "string (required)",
  note: "string[] (required, >= 1 non-empty paragraph)",
  whyIndiaFirst: "string (optional)",
});

/**
 * The published note, or `null` while the copy is unwritten.
 *
 * Example of a COMPLETE value (illustrative shape only — do not ship this text):
 *   export const FOUNDER_NOTE = {
 *     name: "…",
 *     role: "Founder",
 *     note: ["…", "…"],
 *     whyIndiaFirst: "…",
 *   };
 */
export const FOUNDER_NOTE = null;

/**
 * Content-presence guard. TRUE only for a note complete enough to publish — every required field
 * present and non-blank. A partially filled object renders NOTHING, so a work-in-progress edit
 * can never reach production as a half-written personal statement.
 */
export function founderNoteReady(value) {
  return Boolean(
    value
    && typeof value === "object"
    && typeof value.name === "string" && value.name.trim()
    && typeof value.role === "string" && value.role.trim()
    && Array.isArray(value.note) && value.note.length > 0
    && value.note.every((paragraph) => typeof paragraph === "string" && paragraph.trim()),
  );
}

export default FOUNDER_NOTE;
